import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { OrderProto } from "@noom/noom-contracts/noom_contracts/billing/bms/orders/orders_service";
import { useSelector } from "react-redux";
import { CoreReduxState } from "../store";
import { MED_SKU } from "src/utils/medEligibility";
import { BillingUserProto } from "@noom/noom-contracts/noom_contracts/billing/user/billing_user";
import { BillingCyclePreviewProto } from "@noom/noom-contracts/noom_contracts/billing/bms/subscriptions/billing_cycle_preview";
import { CustomerDetailsProto } from "@noom/noom-contracts/noom_contracts/billing/bms/common/shared";
import { OrderOperationProto } from "@noom/noom-contracts/noom_contracts/billing/bms/orders/order_operation";
import { MoneyProto } from "@noom/noom-contracts/noom_contracts/lib/money";

export type SaleItemInfo = {
  medSku?: MED_SKU;
  saleItemId?: string;
  trialSaleItemId?: string;
  planName?: string;
  legacyNoomPlanId?: string;
  pspPlanId?: string;
};

export type PrefetchedEltv = {
  eltvValue: number;
};

export type CheckoutOrdersState = {
  saleItem?: SaleItemInfo;
  customerDetails?: CustomerDetailsProto;
  operation?: OrderOperationProto;
  billingUser?: BillingUserProto;
  order?: OrderProto;
  initialBillingCycle?: BillingCyclePreviewProto;
  recurringBillingCycle?: BillingCyclePreviewProto;
  refundAmount?: MoneyProto;
  hasInitializationFailed?: boolean;
  prefetchedEltv?: PrefetchedEltv | null;
};

const checkoutOrdersStateSlice = createSlice({
  name: "checkoutOrdersState",
  initialState: {} as CheckoutOrdersState,
  reducers: {
    setCheckoutOrders: (state, action: PayloadAction<CheckoutOrdersState>) => {
      return {
        ...state,
        ...action.payload,
      };
    },
    setCheckoutSaleItem: (state, action: PayloadAction<SaleItemInfo>) => {
      return {
        ...state,
        saleItem: action.payload,
        billingAddress: undefined,
        order: undefined,
      };
    },
    setCheckoutCustomerDetails: (
      state,
      action: PayloadAction<CustomerDetailsProto>
    ) => {
      return {
        ...state,
        customerDetails: action.payload,
      };
    },
    setCheckoutOrderOperation: (
      state,
      action: PayloadAction<OrderOperationProto>
    ) => {
      return {
        ...state,
        operation: action.payload,
      };
    },
    setBillingUser: (state, action: PayloadAction<BillingUserProto>) => {
      return {
        ...state,
        billingUser: action.payload,
      };
    },
    setHasInitializationFailed: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        hasInitializationFailed: action.payload,
      };
    },
    setPrefetchedEltv: (
      state,
      action: PayloadAction<PrefetchedEltv | null | undefined>
    ) => {
      return {
        ...state,
        prefetchedEltv: action.payload,
      };
    },
  },
});

export const {
  setCheckoutOrders,
  setCheckoutSaleItem,
  setCheckoutCustomerDetails,
  setCheckoutOrderOperation,
  setBillingUser,
  setHasInitializationFailed,
  setPrefetchedEltv,
} = checkoutOrdersStateSlice.actions;
export default checkoutOrdersStateSlice;

export function useCheckoutOrders() {
  return useSelector((state: CoreReduxState) => state.checkoutOrders);
}
